import * as Sentry from '@sentry/react';
import { CHUNK_LOAD_ERROR_PATTERN } from '../components/isChunkLoadError';
import { wasChunkReloadDispatched } from './chunkReload';

// Configuração do Sentry para monitoramento de erros e performance
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  
  // Configurações de ambiente
  environment: import.meta.env.MODE, // 'development' ou 'production'
  
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  
  // Sem replayIntegration: para ter o vídeo de um erro ele grava a página o tempo
  // todo, em toda sessão, e pesava em cada clique. O erro continua chegando com
  // stack trace e breadcrumbs.
  integrations: [
    Sentry.browserTracingIntegration(),
  ],
  
  // Tracing - Performance monitoring
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 100% em dev, 10% em prod
  
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    "localhost", 
    /^https:\/\/iecearkpuljixdhhalmc\.supabase\.co/, // Supabase
    /^https:\/\/.*\.vercel\.app/, // Se deploy no Vercel
    /^https:\/\/.*\.netlify\.app/, // Se deploy no Netlify
  ],
  
  // Filtros para reduzir ruído
  beforeSend(event) {
    const failure = event.exception?.values?.[0];

    // Chunk de rota lazy que sumiu num deploy. Quando o reload automático foi
    // disparado (chunkReload), a aba já se recuperou sozinha — é ruído esperado
    // a cada publicação, não um bug. Se o reload NÃO foi disparado, o erro
    // persistiu: aí queremos ver, porque indica deploy realmente quebrado.
    if (failure?.value && CHUNK_LOAD_ERROR_PATTERN.test(failure.value)) {
      if (import.meta.env.DEV || wasChunkReloadDispatched()) {
        return null;
      }
      event.level = 'warning';
      event.tags = { ...event.tags, chunk_load_error: 'persistent' };
      return event;
    }

    // Filtrar erros de desenvolvimento
    if (import.meta.env.DEV && event.exception) {
      const error = event.exception.values?.[0];
      // Falsos positivos do React Fast Refresh (Vite HMR): quando vários
      // arquivos são salvos quase ao mesmo tempo, o react-refresh força um
      // re-render síncrono (flushSync) que pode colidir com um render em
      // andamento e disparar "Invalid hook call" / "Cannot read properties
      // of null (reading 'useState')". Isso só acontece em dev, some sozinho
      // e não existe em build de produção (Fast Refresh não vai pro bundle).
      const stack = error?.stacktrace?.frames?.map(f => f.filename).join(' ') || '';
      const isFastRefreshCrash =
        error?.value?.includes("reading 'useState'") ||
        error?.value?.includes('Invalid hook call') ||
        stack.includes('@react-refresh') ||
        stack.includes('react-refresh');
      if (isFastRefreshCrash) {
        return null;
      }
    }
    return event;
  },
  
  // Tags globais para organização
  initialScope: {
    tags: {
      component: "react-frontend",
      version: "1.0.0",
    },
  },
});

export default Sentry;

// Hook para capturar erros de componentes React
export const SentryErrorBoundary = Sentry.withErrorBoundary;

// Função para capturar erros manuais
export const captureError = (error: Error, context?: Record<string, any>) => {
  Sentry.withScope((scope) => {
    if (context) {
      Object.keys(context).forEach((key) => {
        scope.setExtra(key, context[key]);
      });
    }
    Sentry.captureException(error);
  });
};

// Função para adicionar contexto do usuário
export const setUserContext = (user: { id: string; email?: string; name?: string }) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
  });
};

// Função para adicionar breadcrumbs customizados
export const addBreadcrumb = (message: string, category: string, data?: Record<string, any>) => {
  Sentry.addBreadcrumb({
    message,
    category,
    level: 'info',
    data,
  });
};

// Performance monitoring para ações importantes  
export const startTransaction = (name: string, op: string) => {
  return Sentry.startSpan({ name, op }, () => {
    // Retorna o span para uso manual
  });
}; 